import getValue from './getValue';
import getTagValues from './getTagValues';

// Helper function to conditionally set a value on an object
const setOptionalValue = (obj, key, value) => {
  if (value) {
    obj[key] = value;
  }
};

// Helper function to extract product dimensions
const extractProductDimensions = (product) => {
  const dimensions = [35, 36, 37, 38, 39, 40, 48, 49, 50, 91, 152, 153, 154];
  const dimensionData = {};

  dimensions.forEach((dim) => {
    const value = getValue(
      product,
      `[data-tag-product-dimension${dim}]`,
      `product-dimension${dim}`
    );
    if (value) {
      dimensionData[`dimension${dim}`] = value;
    }
  });

  const cd176 = getValue(product, '[data-tag-product-dimensioncd176]', 'product-dimensioncd176');
  if (cd176) {
    dimensionData.dimensioncd176 = cd176;
  }

  return dimensionData;
};

// Extract basic product information
const extractProductInfo = (product, node, isBasicProduct = false) => {
  const productInfo = {};
  const sourceNode = isBasicProduct ? node : product;

  setOptionalValue(
    productInfo,
    'name',
    getValue(sourceNode, '[data-tag-product-name]', 'product-name')
  );
  setOptionalValue(productInfo, 'id', getValue(sourceNode, '[data-tag-product-id]', 'product-id'));
  setOptionalValue(
    productInfo,
    'category',
    getValue(sourceNode, '[data-tag-product-category]', 'product-category')
  );
  setOptionalValue(
    productInfo,
    'brand',
    getValue(sourceNode, '[data-tag-product-brand]', 'product-brand')
  );
  setOptionalValue(
    productInfo,
    'variant',
    getValue(sourceNode, '[data-tag-product-variant]', 'product-variant')
  );

  const price = getValue(sourceNode, '[data-tag-product-price]', 'product-price');
  productInfo.price = price || '0.00';

  setOptionalValue(
    productInfo,
    'position',
    getValue(sourceNode, '[data-tag-product-position]', 'product-position')
  );

  return productInfo;
};

// Handle product list assignment
const assignProductList = (dataFromDom, productInfo, productList, dataType) => {
  if (!productList) return;

  if (dataType === 'productClick' || dataType === 'productVariantClick') {
    dataFromDom.list = productList;
  } else if (dataType === 'productImpression') {
    productInfo.list = productList;
  }
};

// Extract all product data including dimensions
const extractFullProductData = (product) => {
  const productInfo = extractProductInfo(product, null);
  const dimensions = extractProductDimensions(product);
  const productList = getValue(product, '[data-tag-product-list]', 'product-list');

  return { productInfo: { ...productInfo, ...dimensions }, productList };
};

// Handle product-related events
const handleProductEvents = (node, data, dataFromDom) => {
  const productsNodes = node.querySelectorAll('[data-tag-product-identifier]');
  dataFromDom.products = [];

  const currency = getValue(node, '[data-tag-product-currency]', 'product-currency');
  setOptionalValue(dataFromDom, 'currency', currency);

  for (const product of productsNodes) {
    const { productInfo, productList } = extractFullProductData(product);
    assignProductList(dataFromDom, productInfo, productList, data.type);
    dataFromDom.products.push(productInfo);
  }
};

// Extract promotion information
const extractPromotionInfo = (promotion) => {
  const promotionInfo = {};

  setOptionalValue(
    promotionInfo,
    'name',
    getValue(promotion, '[data-tag-promotion-name]', 'promotion-name')
  );
  setOptionalValue(
    promotionInfo,
    'id',
    getValue(promotion, '[data-tag-promotion-id]', 'promotion-id')
  );
  setOptionalValue(
    promotionInfo,
    'creative',
    getValue(promotion, '[data-tag-promotion-creative]', 'promotion-creative')
  );
  setOptionalValue(
    promotionInfo,
    'position',
    getValue(promotion, '[data-tag-promotion-position]', 'promotion-position')
  );

  return promotionInfo;
};

// Handle promotion-related events
const handlePromotionEvents = (node, dataFromDom) => {
  const promotionNodes = node.querySelectorAll('[data-tag-promotion-identifier]');
  dataFromDom.promotions = [];

  for (const promotion of promotionNodes) {
    const promotionInfo = extractPromotionInfo(promotion);
    dataFromDom.promotions.push(promotionInfo);
  }
};

// Extract add to cart product dimensions
const extractAddToCartDimensions = (node) => {
  const dimensions = [36, 38, 48, 49, 59, 91];
  const dimensionData = {};

  dimensions.forEach((dim) => {
    const value = getValue(node, `[data-tag-product-dimension${dim}]`, `product-dimension${dim}`);
    if (value) {
      dimensionData[`dimension${dim}`] = value;
    }
  });

  // Special handling for dimension49
  if (dimensionData.dimension49) {
    const handBuypageURL = new URLSearchParams(window.location.search);
    const handBuylastURL = handBuypageURL.has('utm_medium');
    if (handBuylastURL === true) {
      dimensionData.dimension49 = 'activated';
    }
  }

  const cd176 = getValue(node, '[data-tag-product-dimensioncd176]', 'product-dimensioncd176');
  if (cd176) {
    dimensionData.dimensioncd176 = cd176;
  }

  return dimensionData;
};

// Handle add to cart event
const handleAddToCart = (node, eventName, dataFromDom) => {
  const ecommerce = {};
  const add = { products: [] };

  const productInfo = extractProductInfo(null, node, true);
  productInfo.price = productInfo.price === '0.00' ? 'none' : productInfo.price;

  setOptionalValue(
    productInfo,
    'quantity',
    getValue(node, '[data-tag-product-quantity]', 'product-quantity')
  );

  const dimensions = extractAddToCartDimensions(node);
  add.products.push({ ...productInfo, ...dimensions });

  const currency = getValue(node, '[data-tag-product-currency]', 'product-currency');
  setOptionalValue(ecommerce, 'currencyCode', currency);

  ecommerce.add = add;
  dataFromDom.ecommerce = ecommerce;
  dataFromDom.event_name = eventName;

  setOptionalValue(
    dataFromDom,
    'productInfo',
    getValue(node, '[data-tag-product-info]', 'product-info')
  );
  setOptionalValue(dataFromDom, 'storeName', getValue(node, '[data-tag-store-name]', 'store-name'));
  setOptionalValue(dataFromDom, 'step', getValue(node, '[data-tag-step]', 'step'));
};

// Handle social interaction event
const handleSocialIntEvent = (node, eventName, dataFromDom) => {
  dataFromDom.event_name = eventName;
  dataFromDom.ecommerce = getValue(node, '[data-tag-ecommerce]', 'ecommerce');

  setOptionalValue(dataFromDom, 'network', getValue(node, '[data-tag-network]', 'network'));
  setOptionalValue(
    dataFromDom,
    'social_network',
    getValue(node, '[data-tag-social-network]', 'social-network')
  );
  setOptionalValue(
    dataFromDom,
    'social_action',
    getValue(node, '[data-tag-social-action]', 'social-action')
  );

  dataFromDom.target = window.location.href;
};

// Handle user action event
const handleUserActionEvent = (node, eventName, dataFromDom) => {
  setOptionalValue(dataFromDom, 'event_name', eventName);
  setOptionalValue(
    dataFromDom,
    'click_area',
    getValue(node, '[data-tag-click-area]', 'click-area')
  );
  setOptionalValue(
    dataFromDom,
    'event_detail',
    getValue(node, '[data-tag-event-detail]', 'event-detail')
  );
  setOptionalValue(
    dataFromDom,
    'breadcrumb',
    getValue(node, '[data-tag-breadcrumb]', 'breadcrumb')
  );
  setOptionalValue(
    dataFromDom,
    'productInfo',
    getValue(node, '[data-tag-product-info]', 'product-info')
  );
  setOptionalValue(dataFromDom, 'step', getValue(node, '[data-tag-step]', 'step'));
  setOptionalValue(
    dataFromDom,
    'article_name',
    getValue(node, '[data-tag-article-name]', 'article-name')
  );
  setOptionalValue(
    dataFromDom,
    'module_name',
    getValue(node, '[data-tag-module-name]', 'module-name')
  );
  setOptionalValue(dataFromDom, 'cta_name', getValue(node, '[data-tag-cta-name]', 'cta-name'));
  setOptionalValue(dataFromDom, 'link_url', getValue(node, '[data-tag-link-url]', 'link-url'));
  setOptionalValue(
    dataFromDom,
    'service_name',
    getValue(node, '[data-tag-service-name]', 'service-name')
  );
  setOptionalValue(
    dataFromDom,
    'service_category',
    getValue(node, '[data-tag-service-category]', 'service-category')
  );
};

// Check if event type is product-related
const isProductEvent = (type) => {
  return type === 'productClick' || type === 'productImpression' || type === 'productVariantClick';
};

// Check if event type is promotion-related
const isPromotionEvent = (type) => {
  return type === 'promotionClick' || type === 'promotionImpression';
};

/**
 * Returns all data it can find in the dom for an analytics action.
 * @param {Node} node Parent Node to query for data. Closest [data-tag-wrapper] to Node which triggered the event.
 * @param {Object} data Base data send by the Node which triggered the event. This data will be merged with the data we
 *   find in the Dom.
 */
export default function createDataObjectBasedOnDom(node, data) {
  const dataFromDom = getTagValues(node, ['category', 'action', 'label', 'currency', 'name']);
  const eventName = getValue(node, '[data-tag-event-name]', 'event-name');

  if (isProductEvent(data.type)) {
    handleProductEvents(node, data, dataFromDom);
  }

  if (isPromotionEvent(data.type)) {
    handlePromotionEvents(node, dataFromDom);
  }

  if (data.type === 'addToCart') {
    handleAddToCart(node, eventName, dataFromDom);
  }

  if (data.type === 'socialIntEvent') {
    handleSocialIntEvent(node, eventName, dataFromDom);
  }

  if (data.type === 'userActionEvent') {
    handleUserActionEvent(node, eventName, dataFromDom);
  }

  return { ...data, ...dataFromDom };
}
